import { useMutation, useQuery } from '@tanstack/react-query';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { AuthApi } from 'api';
import type { Admin, User, Duplicate, FindAccount } from 'api/methods/AuthApi';

/**
 * [Auth] Query Key 생성
 * --
 */
export const AuthQueryKeys = createQueryKeys('auth', {
  socialLogin: (params?: { [key: string]: string | number }) => [
    'social',
    params,
  ],
  verifyPrev: (params?: { [key: string]: string | number }) => [
    'verfiyPrev',
    params,
  ],
  verify: (params?: { [key: string]: string | number }) => ['verfiy', params],
});

/**
 * [Auth] 관리자 로그인
 * --
 */
export const useAdminLogin = () => {
  const { mutateAsync: loginAdmin, ...result } = useMutation({
    mutationFn: (variables: Admin) => AuthApi.adminLogin(variables),
  });
  return { loginAdmin, ...result };
};

/**
 * [Auth] 유저 로그인
 * --
 */
export const useUserLogin = () => {
  const { mutateAsync: loginUser, ...result } = useMutation({
    mutationFn: (variables: User) => AuthApi.userLogin(variables),
  });

  return { loginUser, ...result };
};

/**
 * [Auth] 토큰 체크
 * --
 */
export const useAuthCheck = () => {
  const { mutateAsync: authCheck, ...result } = useMutation({
    mutationFn: () => AuthApi.authCheck(),
  });

  return { authCheck, ...result };
};

/**
 * [Auth] 중복검사
 * --
 */
export const useDuplicateCheck = () => {
  const { mutateAsync: duplicate, ...result } = useMutation({
    mutationFn: (variables: Duplicate) => AuthApi.duplicateCheck(variables),
  });

  return { duplicate, ...result };
};

/**
 * [Auth] 소셜로그인 회원 정보조회
 * --
 */
export const useSocialLogin = (params?: { [key: string]: string | number }) => {
  const data = useQuery({
    queryKey: AuthQueryKeys.socialLogin(params).queryKey,
    queryFn: () => AuthApi.socialLogin(params),
    enabled: !!params,
  });

  return data;
};

/**
 * [Auth] 나이스 토큰 발급
 * --
 */
export const useVerifyPrev = () => {
  const data = useQuery({
    queryKey: AuthQueryKeys.verifyPrev().queryKey,
    queryFn: () => AuthApi.verifyPrev(),
  });

  return data;
};

/**
 * [Auth] 나이스 사용자 정보 조회
 * --
 */
export const useVerify = (params?: { [key: string]: string | number }) => {
  const data = useQuery({
    queryKey: AuthQueryKeys.verify(params).queryKey,
    queryFn: () => AuthApi.verify(params),
    enabled: !!params,
  });

  return data;
};

/**
 * [Auth] 계정 찾기
 * --
 */
export const useFindAccount = () => {
  const { mutateAsync: findAccount, ...result } = useMutation({
    mutationFn: (variables: FindAccount) => AuthApi.findAccount(variables),
  });

  return { findAccount, ...result };
};
